import type { DigitalTwinSettingCreationRequest } from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { useReducer } from 'react'
import type { CreateSettingData } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import { getSettingTarget, toEditableSetting } from 'ui/components/DigitalTwinSettingEditor/editorSetting.helper'
import type { ShownInSplitView, SplitViewSelection } from 'views/DigitalTwinSettingsView/settingEditor.types'
import type { SplitViewPane } from './splitViewPane.types'

type SplitViewEditorState = {
  pane: SplitViewPane
  /** Changes whenever a setting was modified, so the property's settings are fetched again. */
  historyRevision: number
}

type SplitViewEditorAction =
  | { type: 'openForm'; setting: CreateSettingData }
  | { type: 'modified' }
  | { type: 'saved'; submittedSetting: DigitalTwinSettingCreationRequest; savedSetting: DigitalTwinSetting | null }
  | { type: 'copyChanged'; copy: CreateSettingData | null }

function getInitialState(selection: SplitViewSelection): SplitViewEditorState {
  const pane: SplitViewPane =
    selection.kind === 'property'
      ? { pane: 'property', property: selection.property }
      : { pane: 'form', setting: selection.setting, choosesProperty: true, copy: null }
  return { pane, historyRevision: 0 }
}

/** The form for a setting; by default on a known property, as when it is opened from the split view itself. */
function formPane(setting: CreateSettingData, choosesProperty = false): SplitViewPane {
  return { pane: 'form', setting, choosesProperty, copy: null }
}

function splitViewEditorReducer(state: SplitViewEditorState, action: SplitViewEditorAction): SplitViewEditorState {
  switch (action.type) {
    case 'openForm':
      return { ...state, pane: formPane(action.setting) }
    case 'modified':
      return { ...state, historyRevision: state.historyRevision + 1 }
    case 'saved': {
      if (action.savedSetting) {
        const choosesProperty = state.pane.pane === 'form' && state.pane.choosesProperty
        return { ...state, pane: formPane(toEditableSetting(action.savedSetting), choosesProperty) }
      }
      const property = getSettingTarget(action.submittedSetting)
      return property ? { ...state, pane: { pane: 'property', property } } : state
    }
    case 'copyChanged':
      return state.pane.pane === 'form' ? { ...state, pane: { ...state.pane, copy: action.copy } } : state
  }
}

/** What a pane shows: a property's settings, or a setting. */
export function getShownSelection(pane: SplitViewPane): SplitViewSelection {
  if (pane.pane === 'property') {
    return { kind: 'property', property: pane.property }
  }
  return { kind: 'setting', setting: pane.copy ?? pane.setting }
}

/** What the main view marks for a pane: for a copy, the setting it was copied from. */
export function getHighlightedSelection(pane: SplitViewPane): SplitViewSelection {
  return getShownSelection(pane.pane === 'form' ? { ...pane, copy: null } : pane)
}

export function getShownInSplitView(pane: SplitViewPane, selectionRevision: number): ShownInSplitView {
  return { selectionRevision, shown: getShownSelection(pane), highlighted: getHighlightedSelection(pane) }
}

type SplitViewEditor = SplitViewEditorState & {
  openForm: (setting: CreateSettingData) => void
  editSetting: (setting: DigitalTwinSetting) => void
  markModified: () => void
  markSaved: (submittedSetting: DigitalTwinSettingCreationRequest, savedSetting: DigitalTwinSetting | null) => void
  setCopy: (copy: CreateSettingData | null) => void
}

/**
 * What the split view shows: one property's settings, or one setting's form. A save keeps the saved
 * setting open, as the server stored it; a save that stored several settings returns to their property.
 */
export function useSplitViewEditor(selection: SplitViewSelection): SplitViewEditor {
  const [state, dispatch] = useReducer(splitViewEditorReducer, selection, getInitialState)

  return {
    ...state,
    openForm: (setting) => dispatch({ type: 'openForm', setting }),
    editSetting: (setting) => dispatch({ type: 'openForm', setting: toEditableSetting(setting) }),
    markModified: () => dispatch({ type: 'modified' }),
    markSaved: (submittedSetting, savedSetting) => dispatch({ type: 'saved', submittedSetting, savedSetting }),
    setCopy: (copy) => dispatch({ type: 'copyChanged', copy }),
  }
}
